import { parseReference, splitAnchorBlock } from '@skjv/common';
import { getBookAbbrev, getLocationTitle, sanitizePdfText } from './pdf-helpers.js';
/** Print take on the app's warm amber/stone scripture styling (index.css
 *  accuracy-checklist / favorite-star amber): scripture prints medium-bold
 *  serif under an amber small-caps label, closed by a warm hairline rule
 *  (UAT 2026-09-04 — Brian: rule + margin, not a tinted card); commentary
 *  prose stays neutral sans. */
const INK = '#1c1917';
const BODY = '#292524';
const MUTED = '#78716c';
const FAINT = '#a8a29e';
const ACCENT = '#b45309';
const RULE = '#d8c8a4';
/** Descending type/margin steps — the page shrinks before it paginates
 *  (Brian: keep the commentary on one page where possible). */
const FIT_STEPS = [
    { title: 21, subtitle: 12.5, body: 11.5, margin: 0.1 },
    { title: 19, subtitle: 12, body: 11, margin: 0.085 },
    { title: 18, subtitle: 11, body: 10, margin: 0.07 },
    { title: 16, subtitle: 10.5, body: 9, margin: 0.06 },
    { title: 15, subtitle: 10, body: 8.5, margin: 0.055 },
    { title: 14, subtitle: 9.5, body: 8, margin: 0.05 },
];
const PT_TO_MM = 25.4 / 72;
const mmLine = (fontSize) => fontSize * 1.15 * PT_TO_MM;
/**
 * AI pericope commentary page — rendered LAST, after teacher and student
 * pages, and only when the study has a commentary attached. Anchored blocks
 * print their range's KJV as a serif-italic pull quote inside a tinted card
 * with an amber accent bar and a small-caps range label; commentary prose
 * runs sans-serif below, so scripture and commentary never blur together.
 */
export async function configureAiCommentaryPage(doc, study, vts) {
    const commentary = study.aiCommentary;
    if (!commentary)
        return;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const paragraphs = sanitizePdfText(commentary.text)
        .split(/\n{2,}/)
        .filter((p) => p.trim())
        // The page renders its own heading — drop the response's in-body title
        // line so it doesn't print twice.
        .filter((p, i) => !(i === 0 && p.trim().startsWith('BIBLE PERICOPE COMMENTARY')));
    // Resolve each anchored block's scripture up front (layout needs lengths).
    const range = commentary.reference.match(/^(.+?)\s+(\d+):/);
    const blocks = [];
    for (const paragraph of paragraphs) {
        const { anchor, anchorText, prose } = splitAnchorBlock(paragraph.trim());
        let scripture = null;
        if (anchor && range) {
            const start = parseReference(`${range[1]} ${range[2]}:${anchor.start}`);
            const end = anchor.end > anchor.start ? parseReference(`${range[1]} ${range[2]}:${anchor.end}`) : undefined;
            if (start) {
                const text = await vts.getVerseText(start, end ?? undefined).catch(() => '');
                scripture = text?.trim() ? sanitizePdfText(text.trim()) : null;
            }
        }
        blocks.push({ anchorText, scripture, prose: anchorText ? prose.trim() : paragraph.trim() });
    }
    // ── fit pass: walk the layout at each step; first one-page fit wins ──────
    const layout = (step, render) => {
        const marginSize = pageWidth * step.margin;
        const maxTextWidth = pageWidth - 2 * marginSize;
        const footerY = pageHeight - pageWidth * 0.1;
        const bodyLimit = footerY - 10;
        const lineHeight = mmLine(step.body);
        const scriptureLine = step.body * 1.3 * PT_TO_MM; // serif quote gets airier leading
        const paragraphGap = lineHeight * 0.8;
        // Scripture-block geometry, derived from the step so the fit pass
        // shrinks it too: label, quote, closing rule, margin under the rule.
        const labelSize = Math.max(6.5, step.body - 3.5);
        const labelCap = labelSize * 0.72 * PT_TO_MM;
        const ruleGap = 2.4; // last scripture baseline → rule
        const ruleMargin = 3.5; // rule → commentary prose
        const scriptureBlockHeight = (scriptureLineCount) => labelCap + 1.8 + scriptureLine * (scriptureLineCount - 0.22) + ruleGap + ruleMargin;
        let fits = true;
        if (render) {
            const pageHeader = `${getBookAbbrev(study.startingLocation)} ${getLocationTitle(study.startingLocation, study.endingLocation)} -- ${study.name}`;
            doc.setFontSize(8.5);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(FAINT);
            doc.text(pageHeader, pageWidth / 2, 10, { align: 'center' });
        }
        let currentY = 26;
        // Header hierarchy: tracked-out amber eyebrow, big serif reference,
        // short accent rule, serif-italic pericope title.
        if (render) {
            doc.setFontSize(9);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(ACCENT);
            doc.text('BIBLE PERICOPE COMMENTARY', pageWidth / 2, currentY, {
                align: 'center',
                charSpace: 0.4,
            });
        }
        currentY += 7.5;
        doc.setFontSize(step.title);
        doc.setFont('times', 'bold');
        const titleLines = doc.splitTextToSize(sanitizePdfText(commentary.reference), maxTextWidth);
        if (render) {
            doc.setTextColor(INK);
            doc.text(titleLines, pageWidth / 2, currentY, { align: 'center' });
        }
        currentY += titleLines.length * (step.title * 1.05 * PT_TO_MM) + 3;
        if (render) {
            doc.setDrawColor(ACCENT);
            doc.setLineWidth(0.5);
            doc.line(pageWidth / 2 - 11, currentY, pageWidth / 2 + 11, currentY);
        }
        currentY += 6.5;
        doc.setFontSize(step.subtitle);
        doc.setFont('times', 'italic');
        if (render) {
            doc.setTextColor(MUTED);
            doc.text(sanitizePdfText(commentary.pericopeTitle), pageWidth / 2, currentY, {
                align: 'center',
            });
        }
        currentY += 11;
        for (const block of blocks) {
            doc.setFontSize(step.body);
            doc.setFont('times', 'bolditalic');
            const scriptureLines = block.scripture
                ? doc.splitTextToSize(block.scripture, maxTextWidth)
                : [];
            doc.setFont('helvetica', 'normal');
            const proseLines = doc.splitTextToSize(block.prose, maxTextWidth);
            const labelHeight = !block.scripture && block.anchorText ? lineHeight : 0;
            const blockHeight = block.scripture
                ? scriptureBlockHeight(scriptureLines.length) + (proseLines.length + 0.72) * lineHeight
                : labelHeight + proseLines.length * lineHeight;
            if (currentY + blockHeight > bodyLimit) {
                fits = false;
                if (!render)
                    return false;
                doc.addPage();
                currentY = marginSize + 6;
            }
            if (block.scripture) {
                // Scripture stand-off: amber small-caps range label, medium-bold
                // serif KJV, closed by a warm hairline rule with margin below it.
                const scrFirstBaseline = currentY + labelCap + 1.8 + scriptureLine * 0.78;
                const ruleY = scrFirstBaseline + (scriptureLines.length - 1) * scriptureLine + ruleGap;
                if (render) {
                    doc.setFontSize(labelSize);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(ACCENT);
                    doc.text((block.anchorText ?? '').toUpperCase(), marginSize, currentY + labelCap, {
                        charSpace: 0.3,
                    });
                    doc.setFontSize(step.body);
                    doc.setFont('times', 'bolditalic');
                    doc.setTextColor(BODY);
                    doc.text(scriptureLines, marginSize, scrFirstBaseline);
                    doc.setDrawColor(RULE);
                    doc.setLineWidth(0.35);
                    doc.line(marginSize, ruleY, marginSize + maxTextWidth, ruleY);
                }
                currentY = ruleY + ruleMargin + lineHeight * 0.72;
            }
            else if (block.anchorText) {
                if (render) {
                    doc.setFontSize(labelSize + 1);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(ACCENT);
                    doc.text(block.anchorText.toUpperCase(), marginSize, currentY, { charSpace: 0.3 });
                }
                currentY += lineHeight;
            }
            if (render) {
                doc.setFontSize(step.body);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(BODY);
                doc.text(proseLines, marginSize, currentY);
            }
            currentY += proseLines.length * lineHeight + paragraphGap;
        }
        if (render) {
            // Disclosure — a quiet line by itself under a hairline rule.
            doc.setDrawColor(RULE);
            doc.setLineWidth(0.2);
            doc.line(pageWidth / 2 - 11, footerY - 4, pageWidth / 2 + 11, footerY - 4);
            doc.setFontSize(8);
            doc.setFont('helvetica', 'italic');
            doc.setTextColor(MUTED);
            doc.text('AI generated commentary. Please read and rate for correctness.', pageWidth / 2, footerY, { align: 'center' });
        }
        return fits;
    };
    const step = FIT_STEPS.find((candidate) => layout(candidate, false)) ?? FIT_STEPS[FIT_STEPS.length - 1];
    layout(step, true);
}
//# sourceMappingURL=pdf-commentary-page.js.map